/**
 * Where an epic is stuck, DERIVED (#4020).
 *
 * Nothing on the platform answered "why is epic X stuck". Measured on prod
 * 2026-09-14 over 30 days: 879 plans, 0 stored as done, 792 never opened a PR,
 * and the terminal cause was spread over `tasks.failure_kind`, `hold_reason` and
 * the newest `task_run_events` row of kind `error` — three relations and a
 * session of hand-written SQL per question. This module is the pure half of the
 * answer: given the facts one query already loads per group (`@developerz/db`
 * `queries/plan-stuck.ts`), it names the funnel stage a group sits at and the
 * stage a whole plan is stuck at. The IO shell (`scripts/infra` `epic` /
 * `epics --stuck`) prints; this file decides.
 *
 * THE STAGES ARE THE FUNNEL PREDICATES THE CENSUS USED, one per branch:
 * group has no task -> undispatched; task pending -> dispatched_pending;
 * assigned/in_progress -> on_box; failed -> failed; cancelled -> cancelled;
 * completed with no linked PR -> completed_no_pr; PR linked and task not merged ->
 * pr_open_unmerged; merged -> merged. `cancelled` is not in the issue's list and
 * is here because prod holds it (12 plans of 880 in the census window) and a
 * cancelled task is neither failed nor undispatched.
 *
 * MERGED IS READ FIRST. `plan_groups.status = 'merged'` has one writer
 * (`markPlanGroupMergedByPr`) and fires on the PR landing; the task row behind a
 * merged group lives on, and a babysit follow-up can flip it to `failed` later.
 * `failure_kind` is NOT cleared on recovery by design (packages/db/CLAUDE.md
 * § conversion read), so reading the task alone would count every merged epic
 * with a noisy tail as stuck. That is the "merged outranks a later failed
 * follow-up" case in `plan-stuck.test.ts`.
 *
 * A PLAN IS STUCK AT ITS WORST UNMERGED GROUP. Groups of one plan are not always
 * a chain — `depends_on` may be empty, so siblings run in parallel — and the
 * census this reproduces counted a plan as `failed` when ANY group's task failed
 * (717 of 882 plans on 2026-09-14, against the census's 718). So the plan's stage
 * is the most-stuck stage among its unmerged groups, in the order of
 * {@link PLAN_GROUP_STUCK_PRECEDENCE}: the terminal-without-a-PR stages first
 * (`failed`, `cancelled`, `completed_no_pr`), then in flight (`pr_open_unmerged`,
 * `on_box`, `dispatched_pending`), then `undispatched`. `all_merged` /
 * `partly_merged` are plan-level facts the group vocabulary cannot say;
 * `cancelled` on the `plans` row outranks everything because `plan cancel`
 * stamps no group on a fully-dispatched plan (see `schema/plans.ts`), so the
 * groups of a cancelled plan still read as whatever they were.
 */

import { TaskStatus } from '../entities/task/status';
import { PlanGroupStatus, PlanStatus } from './plan-projection';
// `PlanStatus` is both the value object and the type; the parameter below takes the TYPE.

/** The funnel stage of ONE plan group. */
export const PlanGroupStuckStage = {
  /** No task behind the group — never dispatched (pending / held), or its task link was cleared. */
  Undispatched: 'undispatched',
  /** A task exists and is queued; no box has claimed it. */
  DispatchedPending: 'dispatched_pending',
  /** A box holds it (`assigned` / `in_progress`). */
  OnBox: 'on_box',
  /** The task failed. `failure_kind` says how. */
  Failed: 'failed',
  /** The task or the group was cancelled. */
  Cancelled: 'cancelled',
  /** The task completed and no PR is linked to it. */
  CompletedNoPr: 'completed_no_pr',
  /** A PR is linked (or the task is in a babysit status) and it has not merged. */
  PrOpenUnmerged: 'pr_open_unmerged',
  /** The group's PR landed. */
  Merged: 'merged',
  /**
   * A repair replaced the group (plan 101 D7, `PlanGroupStatus.Superseded`). Discharged:
   * like `merged` it never decides a plan's stage, and unlike `merged` it counts toward
   * neither `partly_merged` nor `all_merged`; its replacement is a group of its own.
   */
  Superseded: 'superseded',
  /**
   * Paused by a person (#6747): not stuck, and reported apart from every stuck stage.
   * Last in {@link PLAN_GROUP_STUCK_PRECEDENCE}, so it decides a plan only when every
   * unmerged group is paused.
   */
  Paused: 'paused',
} as const;
export type PlanGroupStuckStage = (typeof PlanGroupStuckStage)[keyof typeof PlanGroupStuckStage];
export const PLAN_GROUP_STUCK_STAGES: readonly PlanGroupStuckStage[] =
  Object.values(PlanGroupStuckStage);

/** The facts the query loads per group; everything the derivation reads. */
export interface PlanGroupStuckFacts {
  /** `plan_groups.status` as stored (`PlanGroupStatus`). */
  readonly groupStatus: string;
  /** `tasks.status` of the group's task, or null when `task_id` is null / dangling. */
  readonly taskStatus: string | null;
  /** `tasks.pull_request_number IS NOT NULL`. */
  readonly prLinked: boolean;
}

/** The babysit statuses: a PR exists on GitHub by construction, linked or not. */
const PR_OPEN_STATUSES: ReadonlySet<string> = new Set([
  TaskStatus.AwaitingCi,
  TaskStatus.AwaitingReview,
  TaskStatus.Mergeable,
]);

/**
 * One group's stage. Total over the task status machine: a status outside it
 * THROWS naming the value rather than landing in a bucket that looks like an
 * answer.
 */
export function derivePlanGroupStuckStage(f: PlanGroupStuckFacts): PlanGroupStuckStage {
  // Superseded BEFORE the task-level merged shortcut: a superseded group's task row lives
  // on, and a revived one that later merges must not count the discharged group as merged.
  if (f.groupStatus === PlanGroupStatus.Superseded) return PlanGroupStuckStage.Superseded;
  if (f.groupStatus === PlanGroupStatus.Merged || f.taskStatus === TaskStatus.Merged) {
    return PlanGroupStuckStage.Merged;
  }
  if (f.groupStatus === PlanGroupStatus.Cancelled) return PlanGroupStuckStage.Cancelled;
  if (f.taskStatus === null) return PlanGroupStuckStage.Undispatched;
  switch (f.taskStatus) {
    case TaskStatus.Pending:
      return PlanGroupStuckStage.DispatchedPending;
    case TaskStatus.Assigned:
    case TaskStatus.InProgress:
      return PlanGroupStuckStage.OnBox;
    case TaskStatus.Failed:
      return PlanGroupStuckStage.Failed;
    case TaskStatus.Cancelled:
      return PlanGroupStuckStage.Cancelled;
    case TaskStatus.Completed:
      return f.prLinked ? PlanGroupStuckStage.PrOpenUnmerged : PlanGroupStuckStage.CompletedNoPr;
    case TaskStatus.Paused:
      return PlanGroupStuckStage.Paused;
    default:
      if (PR_OPEN_STATUSES.has(f.taskStatus)) return PlanGroupStuckStage.PrOpenUnmerged;
      throw new Error(
        `derivePlanGroupStuckStage: tasks.status '${f.taskStatus}' is outside the task status ` +
          'machine — add its branch here rather than letting it fall into a bucket.',
      );
  }
}

/** The stage of a whole plan: the group stages minus `merged`, plus the two plan-level facts. */
export const PlanStuckStage = {
  Undispatched: PlanGroupStuckStage.Undispatched,
  DispatchedPending: PlanGroupStuckStage.DispatchedPending,
  OnBox: PlanGroupStuckStage.OnBox,
  Failed: PlanGroupStuckStage.Failed,
  Cancelled: PlanGroupStuckStage.Cancelled,
  CompletedNoPr: PlanGroupStuckStage.CompletedNoPr,
  PrOpenUnmerged: PlanGroupStuckStage.PrOpenUnmerged,
  Paused: PlanGroupStuckStage.Paused,
  /** Some groups merged, not all — stuck at the first unmerged one. */
  PartlyMerged: 'partly_merged',
  /** Every group merged. The only "not stuck" stage. */
  AllMerged: 'all_merged',
} as const;
export type PlanStuckStage = (typeof PlanStuckStage)[keyof typeof PlanStuckStage];

export interface PlanStuckVerdict {
  readonly stage: PlanStuckStage;
  /** Index (plan order) of the group that decided the stage; null when no single group did. */
  readonly decidingIndex: number | null;
}

/**
 * Most-stuck first. `merged` is absent on purpose: a merged group never decides a
 * plan's stage (it is what `all_merged` / `partly_merged` count). `superseded` is absent
 * too: a discharged group decides nothing, and its replacement is judged in its place.
 */
export const PLAN_GROUP_STUCK_PRECEDENCE: readonly PlanGroupStuckStage[] = [
  PlanGroupStuckStage.Failed,
  PlanGroupStuckStage.Cancelled,
  PlanGroupStuckStage.CompletedNoPr,
  PlanGroupStuckStage.PrOpenUnmerged,
  PlanGroupStuckStage.OnBox,
  PlanGroupStuckStage.DispatchedPending,
  PlanGroupStuckStage.Undispatched,
  PlanGroupStuckStage.Paused,
];

/** The index (plan order) of the most-stuck unmerged group; -1 when every group merged. */
function worstUnmergedIndex(stages: readonly PlanGroupStuckStage[]): number {
  for (const stage of PLAN_GROUP_STUCK_PRECEDENCE) {
    const i = stages.indexOf(stage);
    if (i !== -1) return i;
  }
  return -1;
}

/**
 * The plan's stage from its groups' stages IN PLAN ORDER (`position` ascending);
 * ties on precedence go to the earlier group.
 */
export function derivePlanStuckStage(
  planStatus: PlanStatus,
  stages: readonly PlanGroupStuckStage[],
): PlanStuckVerdict {
  if (planStatus === PlanStatus.Cancelled) {
    return { stage: PlanStuckStage.Cancelled, decidingIndex: null };
  }
  if (stages.length === 0) return { stage: PlanStuckStage.Undispatched, decidingIndex: null };
  const worst = worstUnmergedIndex(stages);
  if (worst === -1) return { stage: PlanStuckStage.AllMerged, decidingIndex: null };
  const anyMerged = stages.some((s) => s === PlanGroupStuckStage.Merged);
  if (anyMerged) return { stage: PlanStuckStage.PartlyMerged, decidingIndex: worst };
  // `stages[worst]` is a group stage other than `merged`, which is a member of the
  // plan vocabulary by construction of `PlanStuckStage`.
  return { stage: stages[worst] as PlanStuckStage, decidingIndex: worst };
}

/** One plan as the funnel reads it: groups in plan order, each with its stage and failure kind. */
export interface PlanStuckFunnelInput {
  readonly status: PlanStatus;
  readonly groups: readonly {
    readonly stage: PlanGroupStuckStage;
    readonly failureKind: string | null;
  }[];
}

export interface PlanStuckFunnelBucket {
  readonly stage: PlanStuckStage;
  readonly plans: number;
  /** The three most frequent `failure_kind`s of the DECIDING group, most frequent first. */
  readonly topFailureKinds: readonly { readonly failureKind: string; readonly plans: number }[];
}

export interface PlanStuckFunnel {
  readonly days: number;
  readonly plans: number;
  /** Largest bucket first; stages with no plan are absent. Sums to `plans`. */
  readonly buckets: readonly PlanStuckFunnelBucket[];
}

const TOP_FAILURE_KINDS = 3;

/** Fold the plans of a window into the per-stage histogram `epics --stuck` prints. */
export function foldPlanStuckFunnel(
  days: number,
  plans: readonly PlanStuckFunnelInput[],
): PlanStuckFunnel {
  const byStage = new Map<PlanStuckStage, { plans: number; kinds: Map<string, number> }>();
  for (const plan of plans) {
    const verdict = derivePlanStuckStage(
      plan.status,
      plan.groups.map((g) => g.stage),
    );
    const bucket = byStage.get(verdict.stage) ?? { plans: 0, kinds: new Map<string, number>() };
    bucket.plans += 1;
    const kind =
      verdict.decidingIndex === null
        ? null
        : (plan.groups[verdict.decidingIndex]?.failureKind ?? null);
    if (kind !== null) bucket.kinds.set(kind, (bucket.kinds.get(kind) ?? 0) + 1);
    byStage.set(verdict.stage, bucket);
  }
  const buckets = [...byStage.entries()]
    .map(([stage, b]) => ({
      stage,
      plans: b.plans,
      topFailureKinds: [...b.kinds.entries()]
        .map(([failureKind, n]) => ({ failureKind, plans: n }))
        .sort((x, y) => y.plans - x.plans || x.failureKind.localeCompare(y.failureKind))
        .slice(0, TOP_FAILURE_KINDS),
    }))
    .sort((x, y) => y.plans - x.plans || x.stage.localeCompare(y.stage));
  return { days, plans: plans.length, buckets };
}
